import { useMemo } from "react";

import { useQuery } from "@apollo/client";

import { RVFlowBlockTypes } from "Components/elements/RVFlow/constants";
import { BlockContainer } from "Components/elements/RVFlow/Shared/FlowNodeBlocks/BlockContainer/BlockContainer";
import { BlockContent } from "Components/elements/RVFlow/Shared/FlowNodeBlocks/BlockContent/BlockContent";
import { BlockTitle } from "Components/elements/RVFlow/Shared/FlowNodeBlocks/BlockTitle/BlockTitle";
import { getJourneyNodeIcon } from "Components/elements/RVFlow/Shared/JourneyNodePresentation";
import { useServerSentEvent } from "Hooks/useServerSentEvent";
// todo, move this component to the right place
import { CampaignStatusTag } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/shared/Components/CampaignStatus/CampaignStatusTag";
import { getCampaignById } from "Services/marketing-api/campaigns/queries";
import { NodeType } from "Types/Journey";
import { isNewOrDuplicate } from "Utils/ParamUtils/ParamUtils";

type ActionBlockType = "Create Call Task" | "Create SMS Task" | "Create Custom Task" | "Send SMS";

const ACTION_BLOCK_ICONS: Record<ActionBlockType, JSX.Element> = {
    "Create Call Task": getJourneyNodeIcon(NodeType.Call),
    "Send SMS": getJourneyNodeIcon(NodeType.SMS),
    "Create SMS Task": getJourneyNodeIcon(NodeType.AgentSMSTask),
    "Create Custom Task": getJourneyNodeIcon(NodeType.AgentSMSTask),
};

const ActionVariantConvert = (title: string, type: ActionBlockType): "Agent" | "Automated" => {
    // this is for a node that is only in the building blocks
    // remove when we remove journeyBuilderCustomSmsTask flag
    if (type === "Send SMS") {
        return RVFlowBlockTypes.Automated;
    }
    if (title === "Automated Message") {
        return RVFlowBlockTypes.Automated;
    }
    return RVFlowBlockTypes.Agent;
};

// TODO: update type to import type { SharedBlockProps } from "Components/elements/RVFlow/RVFlowTypes";
// and follow up updateContactBlock or the IVR blocks work

export function ActionBlock({
    data,
    type = "Create Call Task",
    title = "Action",
}: {
    data?: any;
    type: ActionBlockType;
    title?: string;
}): JSX.Element {
    const isActive = data?.campaign?.id || data?.empty;

    const campaignId = data?.campaign?.id;
    const skipQuery = !campaignId || isNewOrDuplicate(campaignId);
    const { data: campaignData, refetch } = useQuery(getCampaignById, {
        variables: { id: campaignId },
        skip: skipQuery,
        fetchPolicy: "cache-and-network",
    });

    useServerSentEvent("sse", ["campaign.updated"], () => !skipQuery && refetch());

    const actionContent = useMemo(() => {
        const { empty, campaign } = data || {};
        if (empty) {
            return type;
        }

        if (!campaign?.key) {
            return "Choose campaign";
        }
        if (campaignData) {
            return `#${campaignData?.campaignWithUnhiddenJourneys?.friendlyId} ${campaignData?.campaignWithUnhiddenJourneys?.name}`;
        }
        return `#${campaign.friendlyId} ${campaign.name}`;
    }, [data, campaignData, type]);

    const containerVariant = ActionVariantConvert(title, type);

    return (
        <BlockContainer variant={containerVariant} icon={ACTION_BLOCK_ICONS[type]}>
            <BlockTitle nodeData={data} fallBackDisplay={data?.empty ? title : type}></BlockTitle>

            <BlockContent isActive={isActive}>{actionContent}</BlockContent>
            {data?.campaign?.state && (
                <CampaignStatusTag
                    state={campaignData?.campaignWithUnhiddenJourneys?.state || data?.campaign?.state}
                    spaceAbove={true}
                />
            )}
        </BlockContainer>
    );
}
